import { isFunction } from 'lodash';
import { ButtonHTMLAttributes, forwardRef, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { TextInput } from '@/components/inputs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { StatusTrigger } from '@/components/ui/data-table/status-filter/trigger';
import { Select } from '@/components/ui/inputs/select';
import { STATUS_FILTER_TYPES } from '@/lib/constants';
import { OptionItem, StatusFilterType } from '@/types';
import { tv } from '@/utils/styles';

const FILTER_OPTIONS: Record<StatusFilterType, OptionItem> = {
  [STATUS_FILTER_TYPES.AHEAD]: {
    value: STATUS_FILTER_TYPES.AHEAD,
    label: 'Ahead of schedule',
  },
  [STATUS_FILTER_TYPES.DELAYED]: { value: STATUS_FILTER_TYPES.DELAYED, label: 'Delayed' },
  [STATUS_FILTER_TYPES.DESTINATION]: { value: STATUS_FILTER_TYPES.DESTINATION, label: 'At destination' },
  [STATUS_FILTER_TYPES.TRANSIT]: { value: STATUS_FILTER_TYPES.TRANSIT, label: 'In transit' },
  [STATUS_FILTER_TYPES.DISCHARGED]: { value: STATUS_FILTER_TYPES.DISCHARGED, label: 'Discharged' },
  [STATUS_FILTER_TYPES.AWAITING_TRANSSHIPMENT]: {
    value: STATUS_FILTER_TYPES.AWAITING_TRANSSHIPMENT,
    label: 'Awaiting transshipment',
  },
} as const;

const CONFIG: Record<
  StatusFilterType,
  { numberField: { isVisible: boolean; label?: string }; selectField: { isVisible: boolean; label?: string } }
> = {
  [STATUS_FILTER_TYPES.AHEAD]: {
    numberField: {
      isVisible: true,
      label: 'By more than',
    },
    selectField: {
      isVisible: true,
      label: '',
    },
  },
  [STATUS_FILTER_TYPES.DELAYED]: {
    numberField: {
      isVisible: true,
      label: 'By more than',
    },
    selectField: {
      isVisible: true,
      label: '',
    },
  },
  [STATUS_FILTER_TYPES.DESTINATION]: {
    numberField: {
      isVisible: false,
    },
    selectField: {
      isVisible: false,
    },
  },
  [STATUS_FILTER_TYPES.TRANSIT]: {
    numberField: {
      isVisible: false,
    },
    selectField: {
      isVisible: false,
    },
  },
  [STATUS_FILTER_TYPES.DISCHARGED]: {
    numberField: {
      isVisible: false,
    },
    selectField: {
      isVisible: false,
    },
  },
  [STATUS_FILTER_TYPES.AWAITING_TRANSSHIPMENT]: {
    numberField: {
      isVisible: false,
    },
    selectField: {
      isVisible: false,
    },
  },
};

export interface StatusFilterValue {
  type?: StatusFilterType;
  time?: string;
  basis?: 'day' | 'week';
}

export interface StatusFormInputs {
  type?: StatusFilterType | '';
  time?: string;
  basis?: 'day' | 'week' | '';
}

export interface StatusProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  selectedValue?: string;
  placeholder?: string;
  modalPopover?: boolean;
  className?: string;
  value?: StatusFilterValue;
  onSearch?: (value: StatusFilterValue | undefined) => void;
  isOpen?: boolean;
}

const StatusFilter = forwardRef<HTMLButtonElement, StatusProps>(
  (
    {
      selectedValue,
      placeholder = 'Select options',
      onSearch,
      modalPopover = false,
      className,
      value: valueProp,
      isOpen: isOpenProp = false,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(isOpenProp);
    const parsedValue: StatusFormInputs = {
      type: valueProp?.type || 'delayed',
      time: valueProp?.time || '',
      basis: valueProp?.basis || 'day',
    };
    const { handleSubmit, control, formState, watch, setValue } = useForm<StatusFormInputs>({
      values: parsedValue,
    });
    const filterTypeValue = watch('type');

    const isSearchDisabled = !filterTypeValue;

    const handleClear = () => {
      if (isFunction(onSearch)) {
        onSearch(undefined);
      }
    };

    const handleFormSubmit: SubmitHandler<StatusFormInputs> = (formValues) => {
      setIsOpen(!isOpen);
      if (isFunction(onSearch) && formValues) {
        onSearch({
          type: formValues.type || undefined,
          basis: (formValues.basis || undefined) as StatusFilterValue['basis'],
          time: formValues.time,
        });
      }
    };

    const handleTogglePopover = () => {
      setIsOpen((prev) => !prev);
    };

    const formConfig = CONFIG[filterTypeValue || STATUS_FILTER_TYPES.AHEAD];

    useEffect(() => {
      // reset the time fields any time a type is selected that doesn't have time values
      if (
        filterTypeValue &&
        (
          [STATUS_FILTER_TYPES.TRANSIT, STATUS_FILTER_TYPES.DISCHARGED, STATUS_FILTER_TYPES.DESTINATION] as string[]
        ).includes(filterTypeValue)
      ) {
        setValue('time', '');
        setValue('basis', 'day');
      }
    }, [filterTypeValue]);

    const { popoverContent, footer } = styles();

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <StatusTrigger
            selectedValue={selectedValue}
            onTogglePopover={handleTogglePopover}
            onValueChange={handleClear}
            placeholder={selectedValue ? '' : placeholder}
            className={className}
            isOpen={isOpen}
            ref={ref}
          />
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start" onEscapeKeyDown={() => setIsOpen(false)}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className={popoverContent()}>
              <Heading as="h3" variant="h4">
                Filter shipment by status and date
              </Heading>
              <div className="flex flex-col gap-2">
                <Controller
                  control={control}
                  render={({ field: { value, name, onChange } }) => {
                    return (
                      <Select
                        id={name}
                        label="Shipment is"
                        errors={formState.errors}
                        selected={value}
                        onSelect={(val) => onChange(val?.value)}
                        options={[
                          {
                            title: '',
                            items: Object.values(FILTER_OPTIONS),
                          },
                        ]}
                      />
                    );
                  }}
                  name="type"
                />
                <div className="flex gap-2">
                  {formConfig.numberField.isVisible ? (
                    <Controller
                      control={control}
                      name="time"
                      render={({ field: { value, name, onChange } }) => (
                        <TextInput
                          label={formConfig.numberField.label}
                          value={value}
                          type="number"
                          name={name}
                          onChange={onChange}
                        />
                      )}
                    />
                  ) : null}
                  {formConfig.selectField.isVisible ? (
                    <Controller
                      control={control}
                      render={({ field: { value, name, onBlur, onChange } }) => {
                        return (
                          <Select
                            className="mt-5"
                            id={name}
                            label={formConfig.selectField.label}
                            selected={value}
                            onBlur={onBlur}
                            onSelect={(val) => onChange(val?.value || '')}
                            options={[
                              {
                                title: '',
                                items: [
                                  { label: 'Day(s)', value: 'day' },
                                  { label: 'Week(s)', value: 'week' },
                                ],
                              },
                            ]}
                          />
                        );
                      }}
                      name="basis"
                    />
                  ) : null}
                </div>
              </div>
            </div>
            <div className={footer()}>
              <Button size="small" type="button" variant="link" onClick={handleClear}>
                Remove filter
              </Button>
              <Button size="small" type="submit" disabled={isSearchDisabled}>
                Apply
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>
    );
  },
);

StatusFilter.displayName = 'StatusFilter';

const styles = tv({
  slots: {
    popoverContent: 'w-auto p-4',
    footer: 'flex justify-between rounded-b bg-grey-100 p-2 max-md:pl-4 md:p-4',
  },
});

export { StatusFilter };
